import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, X } from 'lucide-react';
import { ApiError, api } from '../lib/api.js';
import { Button, Input, useToast } from '../ui/index.js';

interface Cc {
  email: string;
  name: string | null;
  source: 'email' | 'agent';
}

/**
 * Who is copied on this ticket's emails (074).
 *
 * Everyone on the original email besides the requester and the desk is
 * copied on the desk's replies. Anyone can be taken off at any time --
 * someone copied by mistake, or someone a complaint is about -- and a later
 * reply-all does not put them back. People can be added too.
 */
export function TicketCc({ tenant, number, canEdit }: { tenant: string; number: number; canEdit: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const key = ['ticket-cc', tenant, number];
  const q = useQuery({
    queryKey: key,
    queryFn: () => api.get<{ cc: Cc[] }>(`/api/v1/tenants/${tenant}/tickets/${number}/cc`),
  });
  const [adding, setAdding] = useState(false);
  const [email, setEmail] = useState('');

  const add = useMutation({
    mutationFn: () => api.post(`/api/v1/tenants/${tenant}/tickets/${number}/cc`, { email: email.trim() }),
    onSuccess: () => {
      setEmail('');
      setAdding(false);
      void qc.invalidateQueries({ queryKey: key });
      toast.success('Copied on replies from now on');
    },
    onError: (e) => toast.error('Not added', e instanceof ApiError ? e.message : undefined),
  });
  const remove = useMutation({
    mutationFn: (who: string) => api.del(`/api/v1/tenants/${tenant}/tickets/${number}/cc/${encodeURIComponent(who)}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: key });
      toast.success('No longer copied');
    },
    onError: (e) => toast.error('Not removed', e instanceof ApiError ? e.message : undefined),
  });

  const list = q.data?.cc ?? [];
  return (
    <div className="flex flex-col gap-1">
      {list.length === 0 && !adding && <span className="text-ink-tertiary">Nobody</span>}
      {list.map((c) => (
        <span key={c.email} className="group flex min-w-0 items-center gap-1">
          <span className="truncate" title={c.email}>
            {c.name ?? c.email}
          </span>
          {canEdit && (
            <button
              type="button"
              onClick={() => remove.mutate(c.email)}
              disabled={remove.isPending}
              aria-label={`Stop copying ${c.email}`}
              title="Stop copying them"
              className="shrink-0 rounded-xs p-0.5 text-ink-tertiary hover:text-danger focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand"
            >
              <X aria-hidden className="h-3 w-3" />
            </button>
          )}
        </span>
      ))}
      {canEdit &&
        (adding ? (
          <form
            className="flex items-center gap-1.5"
            onSubmit={(e) => {
              e.preventDefault();
              if (email.trim()) add.mutate();
            }}
          >
            <Input
              size="sm"
              value={email}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
              placeholder="someone@esassoc.com"
              aria-label="Email to copy"
              autoFocus
            />
            <Button size="sm" type="submit" disabled={!email.trim() || add.isPending} loading={add.isPending}>
              Add
            </Button>
          </form>
        ) : (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="flex w-fit items-center gap-1 text-2xs text-ink-tertiary hover:text-brand"
          >
            <Plus aria-hidden className="h-3 w-3" />
            Copy someone
          </button>
        ))}
    </div>
  );
}
